// Uno Party Card Game Server Engine
export class UnoEngine {
  constructor(io, room) {
    this.io = io;
    this.room = room;
    this.deck = [];
    this.discardPile = [];
    this.playerHands = {}; // playerId -> Card[]
    this.currentTurnIndex = 0;
    this.direction = 1; // 1 or -1
    this.currentColor = 'red';
  }

  generateDeck() {
    const colors = ['red', 'blue', 'green', 'yellow'];
    const deck = [];

    colors.forEach((color) => {
      deck.push({ color, value: '0', type: 'number' });
      for (let i = 1; i <= 9; i++) {
        deck.push({ color, value: `${i}`, type: 'number' });
        deck.push({ color, value: `${i}`, type: 'number' });
      }
      // Action Cards
      ['skip', 'reverse', '+2'].forEach((action) => {
        deck.push({ color, value: action, type: 'action' });
        deck.push({ color, value: action, type: 'action' });
      });
    });

    // Wild Cards
    for (let i = 0; i < 4; i++) {
      deck.push({ color: 'wild', value: 'wild', type: 'wild' });
      deck.push({ color: 'wild', value: '+4', type: 'wild' });
    }

    return deck.sort(() => 0.5 - Math.random());
  }

  start() {
    this.deck = this.generateDeck();
    this.playerHands = {};
    this.direction = 1;
    this.currentTurnIndex = 0;

    // Fill bots if solo player
    if (this.room.players.length === 1 && this.room.settings?.fillBots !== false) {
      const bots = [
        { id: 'bot_uno1', name: '🤖 UnoFox', isBot: true, avatar: '🦊' },
        { id: 'bot_uno2', name: '🤖 RoboCard', isBot: true, avatar: '🤖' },
        { id: 'bot_uno3', name: '🤖 WildTiger', isBot: true, avatar: '🐯' }
      ];
      this.room.players.push(...bots);
    }

    // Deal 7 cards to each player
    this.room.players.forEach((p) => {
      this.playerHands[p.id] = this.deck.splice(0, 7);
      this.io.to(p.id).emit('uno_hand', { hand: this.playerHands[p.id] });
    });

    // Flip top card
    let topCard = this.deck.pop();
    while (topCard.color === 'wild') {
      this.deck.unshift(topCard);
      topCard = this.deck.pop();
    }
    this.discardPile = [topCard];
    this.currentColor = topCard.color;

    this.broadcastState();
  }

  checkBotTurn() {
    const cp = this.room.players[this.currentTurnIndex % this.room.players.length];
    if (!cp || !cp.isBot) return;

    setTimeout(() => {
      const hand = this.playerHands[cp.id];
      if (!hand || hand.length === 0) return;
      const topCard = this.discardPile[this.discardPile.length - 1];
      const validIndex = hand.findIndex(c =>
        c.color === 'wild' || c.color === this.currentColor || c.value === topCard?.value
      );

      if (validIndex !== -1) {
        const colors = ['red', 'blue', 'green', 'yellow'];
        const wildColor = colors[Math.floor(Math.random() * colors.length)];
        this.playCard(cp.id, validIndex, wildColor);
      } else {
        this.drawCard(cp.id);
      }
    }, 1000);
  }

  broadcastState() {
    const currentPlayer = this.room.players[this.currentTurnIndex % this.room.players.length];
    this.io.to(this.room.code).emit('uno_state', {
      topCard: this.discardPile[this.discardPile.length - 1],
      currentColor: this.currentColor,
      currentTurnId: currentPlayer?.id,
      currentTurnName: currentPlayer?.name,
      handCounts: this.room.players.map((p) => ({
        id: p.id,
        name: p.name,
        count: this.playerHands[p.id]?.length || 0,
        avatar: p.avatar
      }))
    });

    this.checkBotTurn();
  }

  playCard(playerId, cardIndex, chosenWildColor = null) {
    const currentPlayer = this.room.players[this.currentTurnIndex % this.room.players.length];
    if (!currentPlayer || currentPlayer.id !== playerId) return;

    const hand = this.playerHands[playerId];
    if (!hand || !hand[cardIndex]) return;

    const card = hand[cardIndex];
    const topCard = this.discardPile[this.discardPile.length - 1];

    // Validate Move
    const isValid =
      card.color === 'wild' ||
      card.color === this.currentColor ||
      card.value === topCard.value;

    if (!isValid) return;

    // Remove from hand and add to discard
    hand.splice(cardIndex, 1);
    this.discardPile.push(card);
    this.currentColor = card.color === 'wild' ? (chosenWildColor || 'red') : card.color;

    this.io.to(playerId).emit('uno_hand', { hand });

    // Check Win
    if (hand.length === 0) {
      this.io.to(this.room.code).emit('uno_gameOver', { winner: currentPlayer });
      return;
    }

    // Apply Card Effects
    let nextStep = 1;
    if (card.value === 'reverse') {
      this.direction *= -1;
      if (this.room.players.length === 2) nextStep = 2; // In 2-player, reverse acts as skip
    } else if (card.value === 'skip') {
      nextStep = 2;
    } else if (card.value === '+2') {
      const nextPlayer = this.getNextPlayer(1);
      this.drawCardsFor(nextPlayer.id, 2);
      nextStep = 2;
    } else if (card.value === '+4') {
      const nextPlayer = this.getNextPlayer(1);
      this.drawCardsFor(nextPlayer.id, 4);
      nextStep = 2;
    }

    this.advanceTurn(nextStep);
  }

  drawCard(playerId) {
    const currentPlayer = this.room.players[this.currentTurnIndex % this.room.players.length];
    if (!currentPlayer || currentPlayer.id !== playerId) return;

    this.drawCardsFor(playerId, 1);
    this.advanceTurn(1);
  }

  drawCardsFor(playerId, count) {
    if (!this.playerHands[playerId]) return;
    for (let i = 0; i < count; i++) {
      if (this.deck.length === 0) {
        const top = this.discardPile.pop();
        this.deck = this.discardPile.sort(() => 0.5 - Math.random());
        this.discardPile = [top];
      }
      if (this.deck.length > 0) {
        this.playerHands[playerId].push(this.deck.pop());
      }
    }
    this.io.to(playerId).emit('uno_hand', { hand: this.playerHands[playerId] });
  }

  getNextPlayer(step = 1) {
    const len = this.room.players.length;
    let nextIdx = (this.currentTurnIndex + step * this.direction) % len;
    while (nextIdx < 0) nextIdx += len;
    return this.room.players[nextIdx];
  }

  advanceTurn(step = 1) {
    const len = this.room.players.length;
    this.currentTurnIndex = (this.currentTurnIndex + step * this.direction) % len;
    while (this.currentTurnIndex < 0) this.currentTurnIndex += len;
    this.broadcastState();
  }

  destroy() {}
}
